import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

/* ============================================================================
 * v41 — Global DD-MM-YYYY display normalisation
 * ----------------------------------------------------------------------------
 * Storage stays ISO (YYYY-MM-DD) end-to-end. This block only re-formats what
 * the USER sees, so no calculation, sort, filter, export or receipt is
 * altered. Two mechanisms:
 *   1. Monkey-patch Date.prototype.toLocaleDateString() → DD-MM-YYYY when
 *      called with no explicit locale/options (covers legacy call sites).
 *   2. A MutationObserver that walks text nodes and rewrites any bare
 *      YYYY-MM-DD occurrence to DD-MM-YYYY. Input elements are untouched, so
 *      form submissions still carry ISO values.
 * ==========================================================================*/
(function installDdMmYyyyDisplay() {
  const _origToLocaleDateString = Date.prototype.toLocaleDateString;
  Date.prototype.toLocaleDateString = function (locale, opts) {
    if (locale || opts) return _origToLocaleDateString.call(this, locale, opts);
    if (isNaN(this)) return "";
    const dd = String(this.getDate()).padStart(2, "0");
    const mm = String(this.getMonth() + 1).padStart(2, "0");
    return `${dd}-${mm}-${this.getFullYear()}`;
  };

  const ISO_RE = /\b(\d{4})-(\d{2})-(\d{2})(?![\dT-])/g;   // bare YYYY-MM-DD not followed by digit or "T"
  function reformat(text) {
    return text.replace(ISO_RE, (_, y, m, d) => `${d}-${m}-${y}`);
  }
  function visit(node) {
    if (!node) return;
    if (node.nodeType === Node.TEXT_NODE) {
      const parent = node.parentNode;
      if (!parent) return;
      const tag = parent.nodeName;
      // Skip inputs, scripts, styles, code blocks and elements marked opt-out
      if (tag === "SCRIPT" || tag === "STYLE" || tag === "TEXTAREA"
          || tag === "INPUT" || tag === "CODE"
          || parent.getAttribute?.("data-preserve-iso") === "true") return;
      const rewritten = reformat(node.nodeValue);
      if (rewritten !== node.nodeValue) node.nodeValue = rewritten;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.nodeName === "INPUT" || node.nodeName === "TEXTAREA") return;
      for (let child = node.firstChild; child; child = child.nextSibling) {
        visit(child);
      }
    }
  }
  function start() {
    visit(document.body);
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === "characterData") visit(m.target);
        else m.addedNodes && m.addedNodes.forEach(visit);
      }
    });
    mo.observe(document.body, {
      childList: true, subtree: true, characterData: true,
    });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, refetchOnWindowFocus: false },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
